<template>
    <div class="team-sod-eod-review">
        <v-container fluid class="pa-6">
            <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
                <div>
                    <h1 class="text-h5 font-weight-bold mb-1">Team Start of Day / End of Day Review</h1>
                    <p class="text-body-2 grey--text mb-0">
                        Reporting completeness and workload for your team over the selected period.
                    </p>
                </div>
                <v-spacer />
                <SodEodDateRangePicker @change="onRangeChange" />
            </div>

            <v-progress-circular v-if="isLoadingTeamSodEodReport()" indeterminate color="primary" class="d-block mx-auto my-6" />

            <template v-else-if="report">
                <!-- Team stats strip -->
                <v-row dense class="mb-2">
                    <v-col v-for="tile in statTiles" :key="tile.label" cols="6" sm="4" md="2">
                        <v-card outlined class="pa-3 text-center fill-height">
                            <div class="text-h6 font-weight-bold">{{ tile.value }}</div>
                            <div class="text-caption grey--text">{{ tile.label }}</div>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row dense class="mb-4">
                    <v-col cols="12" md="6">
                        <v-card outlined class="pa-3">
                            <div class="text-overline grey--text mb-2">Team Workload Distribution</div>
                            <div v-if="!workloadTotal" class="text-body-2 grey--text">No EOD submissions in this range.</div>
                            <template v-else>
                                <div class="distribution-bar">
                                    <div v-for="seg in workloadSegments" v-show="seg.count > 0" :key="seg.key"
                                        class="distribution-seg" :class="`pulse-${seg.key}`" :style="{ width: `${seg.pct}%` }" />
                                </div>
                                <div class="d-flex flex-wrap mt-2" style="gap: 12px;">
                                    <span v-for="seg in workloadSegments" :key="`legend-${seg.key}`"
                                        class="d-inline-flex align-center text-caption grey--text" style="gap:4px;">
                                        <span class="legend-swatch" :class="`pulse-${seg.key}`"></span>
                                        {{ seg.label }} ({{ seg.count }})
                                    </span>
                                </div>
                            </template>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-card outlined class="pa-3">
                            <div class="text-overline grey--text mb-2">Common Blockers (What Got In The Way)</div>
                            <div v-if="!incompleteReasons.length" class="text-body-2 grey--text">
                                No incomplete-day reasons recorded in this range.
                            </div>
                            <div v-else>
                                <div v-for="reason in incompleteReasons" :key="reason.key" class="reason-row mb-2">
                                    <div class="d-flex justify-space-between text-body-2">
                                        <span>{{ reason.label }}</span>
                                        <span class="grey--text">{{ reason.count }}</span>
                                    </div>
                                    <div class="reason-bar-track">
                                        <div class="reason-bar" :style="{ width: `${reason.pct}%` }" />
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Submitting users -->
                <div class="text-subtitle-1 font-weight-bold mb-2">
                    Submitting Users ({{ submittingMembers.length }})
                </div>
                <div v-if="!submittingMembers.length" class="text-body-2 grey--text mb-4">
                    No submissions from your team in this range yet.
                </div>
                <v-row v-else dense class="mb-4">
                    <v-col v-for="member in submittingMembers" :key="member.user_id" cols="12" sm="6" md="4" lg="3">
                        <SodEodTeamMemberCard :member="member" @click="viewMember(member)" />
                    </v-col>
                </v-row>

                <!-- Non-submitting / needs attention -->
                <div class="text-subtitle-1 font-weight-bold mb-2">
                    Needs Attention ({{ needsAttentionMembers.length }})
                </div>
                <div v-if="!needsAttentionMembers.length" class="text-body-2 grey--text">
                    Everyone reported fully across this period.
                </div>
                <v-simple-table v-else dense>
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Missing SOD</th>
                                <th>Missing EOD</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="member in needsAttentionMembers" :key="member.user_id">
                                <td>{{ member.name }}</td>
                                <td>{{ member.missing_sod_days }} of {{ report.business_days }}</td>
                                <td>{{ member.missing_eod_days }} of {{ report.business_days }}</td>
                                <td class="text-right">
                                    <v-btn text x-small color="primary" @click="viewMember(member)">View report</v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </template>
        </v-container>
    </div>
</template>

<script>
import SodEodDateRangePicker from '../../components/modules/SodEodDateRangePicker.vue';
import SodEodTeamMemberCard from '../../components/modules/SodEodTeamMemberCard.vue';

export default {
    name: 'TeamSodEodReview',
    components: {
        SodEodDateRangePicker,
        SodEodTeamMemberCard,
    },
    inject: {
        teamSodEodReport: { from: 'teamSodEodReport', default: () => () => null },
        getTeamSodEodReport: { from: 'getTeamSodEodReport', default: () => async () => null },
        isLoadingTeamSodEodReport: { from: 'isLoadingTeamSodEodReport', default: () => () => false },
    },
    computed: {
        report() {
            return this.teamSodEodReport();
        },
        statTiles() {
            if (!this.report) return [];
            const t = this.report.team;
            const completionPct = t.completion_rate !== null ? `${Math.round(t.completion_rate * 100)}%` : '—';
            return [
                { label: 'Team members', value: t.member_count },
                { label: 'Needs attention', value: t.users_missing_count },
                { label: 'Tasks completed', value: `${t.completed_count}/${t.planned_count} (${completionPct})` },
                { label: 'Business days', value: this.report.business_days },
            ];
        },
        workloadTotal() {
            if (!this.report) return 0;
            return Object.values(this.report.team.workload_counts || {}).reduce((a, b) => a + b, 0);
        },
        workloadSegments() {
            if (!this.report) return [];
            const counts = this.report.team.workload_counts || {};
            const labels = { comfortable: 'Comfortable', about_right: 'About Right', too_much: 'Too Much' };
            const total = this.workloadTotal || 1;
            return Object.keys(labels).map((key) => ({
                key,
                label: labels[key],
                count: counts[key] || 0,
                pct: ((counts[key] || 0) / total) * 100,
            }));
        },
        incompleteReasons() {
            if (!this.report) return [];
            const labels = {
                higher_priority: 'Higher priority came up',
                waiting_on_someone: 'Waiting on someone',
                larger_than_expected: 'Larger than expected',
                blocked: 'Blocked',
                other: 'Other',
            };
            const counts = this.report.team.incomplete_reason_counts || {};
            const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
            return Object.keys(counts)
                .map((key) => ({ key, label: labels[key] || key, count: counts[key], pct: (counts[key] / total) * 100 }))
                .sort((a, b) => b.count - a.count);
        },
        submittingMembers() {
            if (!this.report) return [];
            return this.report.members
                .filter((m) => m.days_with_sod > 0 || m.days_with_eod > 0)
                .slice()
                .sort((a, b) => {
                    if (a.is_missing !== b.is_missing) return a.is_missing ? -1 : 1;
                    const weight = { too_much: 0, about_right: 1, comfortable: 2 };
                    const aw = weight[a.most_recent_workload_pulse] ?? 3;
                    const bw = weight[b.most_recent_workload_pulse] ?? 3;
                    if (aw !== bw) return aw - bw;
                    return a.name.localeCompare(b.name);
                });
        },
        needsAttentionMembers() {
            if (!this.report) return [];
            return this.report.members
                .filter((m) => m.is_missing)
                .slice()
                .sort((a, b) => (b.missing_sod_days + b.missing_eod_days) - (a.missing_sod_days + a.missing_eod_days));
        },
    },
    methods: {
        onRangeChange(range) {
            this.getTeamSodEodReport(range.dateFrom, range.dateTo);
        },
        viewMember(member) {
            this.$router.push({ name: 'TeamMemberSodEodReport', params: { userId: member.user_id } });
        },
    },
};
</script>

<style scoped>
.distribution-bar {
    display: flex;
    height: 14px;
    border-radius: 4px;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.05);
}

.distribution-seg {
    height: 100%;
}

.pulse-comfortable {
    background: rgba(12, 163, 12, 0.55);
}

.pulse-about_right {
    background: rgba(250, 178, 25, 0.6);
}

.pulse-too_much {
    background: rgba(208, 59, 59, 0.55);
}

.legend-swatch {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 2px;
}

.reason-bar-track {
    height: 6px;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.reason-bar {
    height: 100%;
    background: #7b5ea7;
}
</style>
